import { State, Action, StateContext, Selector, Store, Select } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import * as UserActions from './user.action';
import { UserService } from './user.service';
import { BannerService } from '../../shared/services/banner.service';
import { HelperService } from '../../../../src/app/services/helper/helper.service';
import * as Models from '../../models/commonModels';
import * as UserModels from '../../models/user/user';
import { SaveState } from '../../models/commonModels';
import { AuthenticationService } from '../../shared/services/authentication.service';


export interface UserStateModel {
  GuardianDictionaryList: UserModels.UserDictionaryListModel;
  User: UserModels.User;
  UserEdit: Models.Response<UserModels.UserEditModel>;
  Search: UserModels.UserSearchModel;
  List: Models.ResponseList<UserModels.UserPreviewListModel>;
  Save: SaveState;
  UserPreviewEdit: Models.Response<UserModels.UserPreviewListModel>;
  RemoveResult: Models.ResponseBase;
}

@State<UserStateModel>({
  name: 'userState',
  defaults: {
    GuardianDictionaryList: null,
    UserEdit: null,
    User: null,
    Save: null,
    Search: null,
    List: null,
    UserPreviewEdit: null,
    RemoveResult: null,
  }
})

@Injectable() 
export class UserState {

  constructor(
    private helperService: HelperService,
    private userService: UserService,
    private bannerService: BannerService,
    private authService: AuthenticationService
  ) { }


  @Selector()
  static GuardianDictionaryList(state: UserStateModel) {
    return state.GuardianDictionaryList;
  }

  @Selector()
  static UserSave(state: UserStateModel) {
    return state.Save;
  }

  @Selector()
  static Search(state: UserStateModel) {
    return state.Search;
  }

  @Selector()
  static List(state: UserStateModel) {
    return state.List;
  }

  @Selector()
  static UserEdit(state: UserStateModel) {
    return state.UserEdit;
  }

  @Selector()
  static UserPreviewEdit(state: UserStateModel) {
    return state.UserPreviewEdit;
  }

  @Selector()
  static RemoveResult(state: UserStateModel) {
    return state.RemoveResult;
  }

  @Selector()
  static User(state: UserStateModel) {
    return state.User;
  }

  @Action(UserActions.GetGuardianDictionary)
  getGuardianDictionary(
    context: StateContext<UserStateModel>,
    action: UserActions.GetGuardianDictionary
  ) {

    const errorMessage = 'Błąd pobierania słownika opiekunów.';

    const state = context.getState();

    if (!state.GuardianDictionaryList) {

      context.setState(
        patch({
          GuardianDictionaryList: null
        })
      );

      this.userService.getGuardianDictionary().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              GuardianDictionaryList: ret
            })
          );
        } else {
          this.bannerService.error(errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('UserActions.GetGuardianDictionary', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(UserActions.SetContextUser)
  SetContextUser(
    context: StateContext<UserStateModel>,
    action: UserActions.SetContextUser
  ) {

    const errorMessage = 'Błąd zmiany kontekstu.';

    const state = context.getState();

    this.userService.setContextUser(action.User).subscribe((ret) => {
      if (ret.Success) {
        this.authService.updateCompanyName(ret.User.CompanyName);

        context.setState(
          patch({
            User: ret.User
          })
        );

      } else {
        this.bannerService.error(errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('UserActions.SetContextUser', error);

        this.bannerService.error(errorMessage);
      });

  }

  @Action(UserActions.ResetSave)
  resetSave(
    context: StateContext<UserStateModel>,
    action: UserActions.ResetSave
  ) {

    const state = context.getState();
    context.setState(
      patch({
        Save: new SaveState(null, false, Math.random())
      })
    );

  }

  @Action(UserActions.GetList)
  GetList(
    context: StateContext<UserStateModel>,
    action: UserActions.GetList
  ) {

    const errorMessage = 'Błąd pobierania listy Użytkowników.';

    context.setState(
      patch({
        List: null
      })
    );

    this.userService.getList(action.Search).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            List: ret
          })
        );

        return ret;

      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('UserActions.GetList', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(UserActions.SetSearch)
  SetSearch(
    context: StateContext<UserStateModel>,
    action: UserActions.SetSearch
  ) {

    action.Search.CurrentPage = 1;

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new UserActions.GetList(action.Search));
  }

  @Action(UserActions.SetListPage)
  SetListPage(
    context: StateContext<UserStateModel>,
    action: UserActions.SetListPage
  ) {

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new UserActions.GetList(action.Search));
  }

  @Action(UserActions.ResetSearch)
  ResetSearch(
    context: StateContext<UserStateModel>
  ) {

    const state = context.getState();

    const search = new UserModels.UserSearchModel();

    search.ItemsPerPage = state.Search.ItemsPerPage;

    context.setState(
      patch({
        Search: search
      }));

    context.dispatch(new UserActions.GetList(search));
  }

  @Action(UserActions.Get)
  Get(
    context: StateContext<UserStateModel>,
    action: UserActions.Get
  ) {
    const errorMessage = 'Błąd odczytu danych użytkownika.';

    this.userService.get(action.UserID).subscribe(
      ret => {

        if (ret.Success) {

          context.setState(
            patch({
              UserEdit: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('UserActions.Get', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(UserActions.Set)
  Set(
    context: StateContext<UserStateModel>,
    action: UserActions.Set
  ) {
    const errorMessage = 'Błąd zapisu użytkownika.';

    this.userService.set(action.User).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Zapisano zmiany dla użytkownika ' + ret.Item.Login + '.');

          context.setState(
            patch({
              UserEdit: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('UserActions.Set', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(UserActions.Create)
  Create(
    context: StateContext<UserStateModel>,
    action: UserActions.Set
  ) {
    const errorMessage = 'Błąd zapisu użytkownika.';

    this.userService.create(action.User).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Utworzono użytkownika ' + ret.Item.Login + '.');

          context.setState(
            patch({
              UserEdit: ret
            })
          );
        } else {
          context.setState(
            patch({
              UserEdit: ret
            })
          );
          this.bannerService.error(ret.Message, errorMessage);
        }

      },

      (error) => {

        this.helperService.SaveException('UserActions.Set', error); 
        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(UserActions.SetPreview)
  SetUserPreview(
    context: StateContext<UserStateModel>,
    action: UserActions.SetPreview
  ) {
    const errorMessage = 'Błąd zapisu użytkownika.';

    this.userService.setUserPreview(action.User).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Zapisano zmiany dla użytkownika ' + ret.Item.Login + '.');

          context.setState(
            patch({
              UserPreviewEdit: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('UserActions.SetPreview', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }
 
  @Action(UserActions.Remove)
  Remove(
    context: StateContext<UserStateModel>,
    action: UserActions.Remove
  ) {
    const errorMessage = 'Błąd podczas próby usunięcia użytkownika.';

    this.userService.remove(action.UserID).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Usunięto użytkownika');
          context.setState(
            patch({
              RemoveResult: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('UserActions.Remove', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }
}




